import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { codeLabel } from '../../utils/format';

const fmtDate = v => {
  if (!v) return '–';
  try { return format(parseISO(String(v).slice(0, 10)), 'd MMM yyyy'); } catch { return '–'; }
};

// These keys have to match what /reports/client-notes actually returns. Date, Mood and Behaviour
// were reading created_at / mood / behaviour, none of which the API sends, so all three columns
// had been rendering as "–" on every row.
const COLS = [
  { key: 'note_date',        label: 'Note Date',  render: v => fmtDate(v) },
  { key: 'client_name',      label: 'Client' },
  { key: 'staff_name',       label: 'Staff' },
  { key: 'note_type',        label: 'Note Type',  render: v => codeLabel(v) || '–' },
  { key: 'mood_rating',      label: 'Mood',       render: v => v ? `${v}/5` : '–' },
  { key: 'behaviour_rating', label: 'Behaviour',  render: v => v ? `${v}/5` : '–' },
  {
    key: 'note_content',
    label: 'Content',
    render: v => (
      <span title={v} className="text-xs">
        {(v || '').slice(0, 120)}{(v || '').length > 120 ? '…' : ''}
      </span>
    ),
  },
  {
    key: 'is_private',
    label: 'Private',
    render: v => v
      ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-medium">Private</span>
      : <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-500 font-medium">Shared</span>,
  },
];

export default function ClientNotesReport() {
  const [data, setData]         = useState([]);
  const [clients, setClients]   = useState([]);
  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const [staffId, setStaffId]   = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    if (staffId)  p.set('staff_id', staffId);
    api.get(`/reports/client-notes?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId, staffId]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => String(c.id) === String(clientId));
  const selStaff  = staff.find(s => String(s.id) === String(staffId));

  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
    ...(selStaff  ? [{ label: 'Staff',  value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]  : []),
  ];

  return (
    <ReportLayout
      title="Client Progress Notes"
      data={data}
      filename="client_notes_report"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="flex items-center gap-4 text-sm text-slate-600 font-medium">
        <span>Total notes: <strong className="text-slate-900">{data.length}</strong></span>
      </div>
      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No progress notes found for this period."
      />
    </ReportLayout>
  );
}
